import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Chip } from '@mui/material';
import { WalteroCard, WalteroTypography, WalteroButton } from '../waltero';
import SensorImageThumbnail from '../report/SensorImageThumbnail';
import { usePerformanceData } from '../../context/PerformanceDataContext';
import { formatDate, getSuccessRateColor } from '../../utils/performanceFormatters';
import { sortTableData, getSortIcon, getSortAriaSort, handleSortKeyDown } from '../../utils/reportSorting';
import { hasOrganizationAccess } from '../../utils/auth';
import { ADMIN_ORGANIZATION_ID } from '../../config/api';
import { getSensorEffectiveApplication } from '../../services/apiService';

// Get CSS class for success rate color coding
const getSuccessColorClass = (rate) => {
  if (rate === null || rate === undefined || isNaN(rate)) return 'no-data';
  const numRate = parseFloat(rate);
  if (numRate >= 95) return 'excellent';
  if (numRate >= 85) return 'good';
  if (numRate >= 70) return 'attention';
  return 'critical';
};

const SuccessRateDataTable = ({ onImageClick, onHoverData }) => {
  const { performanceData, handleExportSuccessRate } = usePerformanceData();
  
  console.log('📊 SuccessRateDataTable rendering with performanceData:', performanceData?.length);
  const [sortColumn, setSortColumn] = useState('serialNumber');
  const [sortDirection, setSortDirection] = useState('asc');
  const [deviceApplications, setDeviceApplications] = useState({});

  // Load application data for all devices
  useEffect(() => {
    const loadDeviceApplications = async () => {
      if (!performanceData || performanceData.length === 0) return;
      
      const applicationPromises = performanceData.map(async (device) => {
        try {
          const result = await getSensorEffectiveApplication(device.deviceId);
          return {
            deviceId: device.deviceId,
            application: result.data
          };
        } catch (error) {
          console.error(`Error loading application for device ${device.deviceId}:`, error);
          return {
            deviceId: device.deviceId,
            application: null
          };
        }
      });
      
      try {
        const results = await Promise.all(applicationPromises);
        const applicationsMap = {};
        results.forEach(({ deviceId, application }) => {
          applicationsMap[deviceId] = application;
        });
        setDeviceApplications(applicationsMap);
      } catch (error) {
        console.error('Error loading device applications:', error);
      }
    };

    loadDeviceApplications();
  }, [performanceData]);

  // Process data to create success rate pivot table
  const { successPivotData, dates, summaryStats } = useMemo(() => {
    if (!performanceData || performanceData.length === 0) {
      return { successPivotData: [], dates: [], summaryStats: {} };
    }

    // Get all unique dates from all devices
    const allDates = new Set();
    performanceData.forEach(device => {
      if (device.dailyStats) {
        device.dailyStats.forEach(stat => {
          if (stat.date) allDates.add(stat.date);
        });
      }
    });
    
    const sortedDates = Array.from(allDates).sort().reverse(); // Most recent first
    
    // Create pivot data for each device
    const pivotData = performanceData.map(device => {
      const deviceApp = deviceApplications[device.deviceId];
      const devicePivot = {
        deviceId: device.deviceId,
        serialNumber: device.serialNumber,
        meterName: device.meterName,
        applicationName: deviceApp?.applicationName || 'Loading...'
      };
      
      // Add success rate data for each date
      sortedDates.forEach(date => {
        const dayStats = device.dailyStats?.find(stat => stat.date === date);
        devicePivot[date] = dayStats ? {
          partialSuccessPercentage: dayStats.partialSuccessPercentage,
          binarySuccessPercentage: dayStats.binarySuccessPercentage,
          digitPositionStats: dayStats.digitPositionStats
        } : null;
      });
      
      return devicePivot;
    });

    // Calculate summary statistics for each date
    const summary = {
      median: {},
      critical: {},
      attention: {},
      healthy: {}
    };

    sortedDates.forEach(date => {
      const daySuccessRates = pivotData
        .map(device => device[date]?.partialSuccessPercentage)
        .filter(rate => rate !== null && rate !== undefined && !isNaN(rate));
      
      if (daySuccessRates.length > 0) {
        // Calculate median
        const sorted = [...daySuccessRates].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        summary.median[date] = sorted.length % 2 !== 0 ? 
          sorted[mid] : 
          (sorted[mid - 1] + sorted[mid]) / 2;
        
        // Calculate percentages by category
        const criticalCount = daySuccessRates.filter(rate => rate < 70).length;
        const attentionCount = daySuccessRates.filter(rate => rate >= 70 && rate < 85).length;
        const healthyCount = daySuccessRates.filter(rate => rate >= 85).length;
        
        summary.critical[date] = (criticalCount / daySuccessRates.length) * 100;
        summary.attention[date] = (attentionCount / daySuccessRates.length) * 100;
        summary.healthy[date] = (healthyCount / daySuccessRates.length) * 100;
      } else {
        summary.median[date] = null;
        summary.critical[date] = 0;
        summary.attention[date] = 0;
        summary.healthy[date] = 0;
      }
    });

    return {
      successPivotData: pivotData,
      dates: sortedDates,
      summaryStats: summary
    };
  }, [performanceData, deviceApplications]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };


  const sortedData = sortTableData(successPivotData, sortColumn, sortDirection);
  
  console.log('📊 SuccessRateDataTable processed data:', {
    successPivotData: successPivotData.length,
    dates: dates.length,
    sortedData: sortedData.length,
    sampleDevice: successPivotData[0]
  });

  return (
    <div className="success-rate-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <WalteroTypography variant="h6">Success Rate (Daily)</WalteroTypography>
          <Chip 
            label="Admin only" 
            size="small" 
            sx={{ 
              bgcolor: '#D32F2F', 
              color: 'white', 
              fontSize: '0.7rem',
              fontWeight: 'bold'
            }} 
          />
        </div>
        <WalteroButton 
          onClick={handleExportSuccessRate}
          disabled={performanceData.length === 0}
          variant="outlined"
          size="small"
        >
          Export CSV
        </WalteroButton>
      </div>
      
      <WalteroCard sx={{ overflowX: 'auto' }}>
        <table 
          className="performance-table"
          role="table"
          aria-label="Daily success rate data for all devices"
        >
          <thead>
            <tr role="row">
              <th 
                onClick={() => handleSort('serialNumber')}
                onKeyDown={(e) => handleSortKeyDown(e, 'serialNumber', handleSort)}
                tabIndex={0}
                role="columnheader"
                aria-sort={getSortAriaSort('serialNumber', sortColumn, sortDirection)}
                style={{ cursor: 'pointer' }}
              >
                Device {getSortIcon('serialNumber', sortColumn, sortDirection)}
              </th>
              <th 
                onClick={() => handleSort('meterName')}
                onKeyDown={(e) => handleSortKeyDown(e, 'meterName', handleSort)}
                tabIndex={0}
                role="columnheader"
                aria-sort={getSortAriaSort('meterName', sortColumn, sortDirection)}
                style={{ cursor: 'pointer' }}
              >
                Meter Name {getSortIcon('meterName', sortColumn, sortDirection)}
              </th>
              <th 
                onClick={() => handleSort('applicationName')}
                onKeyDown={(e) => handleSortKeyDown(e, 'applicationName', handleSort)}
                tabIndex={0}
                role="columnheader"
                aria-sort={getSortAriaSort('applicationName', sortColumn, sortDirection)}
                style={{ cursor: 'pointer' }}
              >
                Application {getSortIcon('applicationName', sortColumn, sortDirection)}
              </th>
              <th role="columnheader">Latest Image</th>
              {dates.map(date => (
                <th 
                  key={date} 
                  onClick={() => handleSort(date)}
                  onKeyDown={(e) => handleSortKeyDown(e, date, handleSort)}
                  tabIndex={0}
                  role="columnheader"
                  aria-sort={getSortAriaSort(date, sortColumn, sortDirection)}
                  style={{ cursor: 'pointer' }}
                >
                  {formatDate(date)} {getSortIcon(date, sortColumn, sortDirection)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((device) => {
              const deviceApp = deviceApplications[device.deviceId];
              return (
                <tr key={device.deviceId} role="row">
                <td role="cell">{device.serialNumber}</td>
                <td role="cell">{device.meterName}</td>
                <td role="cell">
                  {deviceApp ? (
                    <Chip
                      label={deviceApp.applicationName}
                      size="small"
                      color={deviceApp.assignmentType === 'explicit' ? 'primary' : 'secondary'}
                      variant={deviceApp.assignmentType === 'explicit' ? 'filled' : 'outlined'}
                      title={
                        deviceApp.assignmentType === 'explicit' 
                          ? 'Explicitly assigned application'
                          : 'Using organization default application'
                      }
                    />
                  ) : (
                    <Chip
                      label="Loading..."
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  )}
                </td>
                <td role="cell">
                  <SensorImageThumbnail 
                    deviceId={device.deviceId} 
                    onImageClick={onImageClick}
                  />
                </td>
                {dates.map(date => (
                  <td 
                    key={date}
                    role="cell"
                    style={{
                      cursor: device[date] !== null && device[date]?.digitPositionStats ? 'pointer' : 'default'
                    }}
                    onMouseEnter={(e) => {
                      if (device[date] !== null && device[date]?.digitPositionStats) {
                        onHoverData && onHoverData({
                          type: 'digitPosition',
                          data: device[date].digitPositionStats,
                          position: { x: e.clientX, y: e.clientY }
                        });
                      }
                    }}
                    onMouseMove={(e) => {
                      if (device[date] !== null && device[date]?.digitPositionStats) {
                        onHoverData && onHoverData({
                          type: 'digitPosition',
                          data: device[date].digitPositionStats,
                          position: { x: e.clientX, y: e.clientY }
                        });
                      }
                    }}
                    onMouseLeave={() => {
                      onHoverData && onHoverData(null);
                    }}
                    aria-label={device[date] ? 
                      `Success rate for ${device.serialNumber} on ${formatDate(date)}: ${device[date].partialSuccessPercentage.toFixed(1)}%` :
                      `No data for ${device.serialNumber} on ${formatDate(date)}`
                    }
                  >
                    {device[date] !== null ? (
                      <span className={`success-rate ${getSuccessColorClass(device[date].partialSuccessPercentage)}`}>
                        {device[date].partialSuccessPercentage.toFixed(1)}% / {device[date].binarySuccessPercentage.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="table-no-data">N/A</span>
                    )}
                  </td>
                ))}
                </tr>
              );
            })}
            
            {/* Summary rows */}
            <tr className="summary-separator">
              <td colSpan={dates.length + 4}></td>
            </tr>
            
            {/* Median row */}
            <tr className="summary-row" role="row">
              <td className="summary-label" role="cell">Median</td>
              <td role="cell"></td>
              <td role="cell"></td>
              <td role="cell"></td>
              {dates.map(date => (
                <td key={date} className="summary-cell" role="cell">
                  {summaryStats.median[date] !== null ? (
                    <span className={`percentage-badge ${getSuccessColorClass(summaryStats.median[date])}`}>
                      {summaryStats.median[date].toFixed(1)}%
                    </span>
                  ) : (
                    <span className="table-no-data">N/A</span>
                  )}
                </td>
              ))}
            </tr>
            
            {/* Count rows */}
            <tr className="summary-separator">
              <td colSpan={dates.length + 4}></td>
            </tr>
            
            {/* Percent Critical row */}
            <tr className="summary-row count-row" role="row">
              <td className="summary-label" role="cell">% Critical</td>
              <td role="cell"></td>
              <td role="cell"></td>
              <td role="cell"></td>
              {dates.map(date => (
                <td key={date} className="summary-cell" role="cell">
                  <span className="count-badge critical-count">
                    {summaryStats.critical[date].toFixed(1)}%
                  </span>
                </td>
              ))}
            </tr>
            
            {/* Percent Attention row */}
            <tr className="summary-row count-row" role="row">
              <td className="summary-label" role="cell">% Attention</td>
              <td role="cell"></td>
              <td role="cell"></td>
              {dates.map(date => (
                <td key={date} className="summary-cell" role="cell">
                  <span className="count-badge attention-count">
                    {summaryStats.attention[date].toFixed(1)}%
                  </span>
                </td>
              ))}
            </tr>
            
            {/* Percent Healthy row */}
            <tr className="summary-row count-row" role="row">
              <td className="summary-label" role="cell">% Healthy</td>
              <td role="cell"></td>
              <td role="cell"></td>
              {dates.map(date => (
                <td key={date} className="summary-cell" role="cell">
                  <span className="count-badge healthy-count">
                    {summaryStats.healthy[date].toFixed(1)}%
                  </span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </WalteroCard>
    </div>
  );
};

SuccessRateDataTable.propTypes = {
  onImageClick: PropTypes.func,
  onHoverData: PropTypes.func
};

SuccessRateDataTable.defaultProps = {
  onImageClick: () => {},
  onHoverData: () => {}
};

export default SuccessRateDataTable;